import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { COMPANY } from "@/lib/api";

const LOGO_URL = "/wre-logo.jpg";

const SEGMENTS = [
  { v: "hgv", label: "HGV" },
  { v: "bus", label: "Bus / PSV" },
  { v: "industrial", label: "Industrial" },
  { v: "marine", label: "Marine" },
];

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  {
    label: "Starter Motors",
    type: "starter-motor",
    children: SEGMENTS.map((s) => ({
      label: `${s.label} Starter Motors`,
      to: `/catalog?type=starter-motor&segment=${s.v}`,
    })),
  },
  {
    label: "Alternators",
    type: "alternator",
    children: SEGMENTS.map((s) => ({
      label: `${s.label} Alternators`,
      to: `/catalog?type=alternator&segment=${s.v}`,
    })),
  },
  { to: "/quote", label: "Get Quote" },
  { to: "/contact", label: "Contact" },
];

function DesktopDropdown({ label, type, children }) {
  return (
    <div className="relative group border-r border-ink-600">
      <button
        type="button"
        data-testid={`nav-dropdown-${type}`}
        className="px-6 py-5 font-mono-tech text-[11px] text-steel-200 group-hover:text-copper group-hover:bg-ink-800 inline-flex items-center gap-1 transition-colors"
      >
        {label}
        <ChevronDown size={12} className="group-hover:rotate-180 transition-transform" />
      </button>
      <div
        className="absolute left-0 top-full min-w-[260px] bg-ink-800 border border-ink-600 border-t-0 shadow-[8px_8px_0_#050E1B] opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-150 z-50"
        role="menu"
      >
        {children.map((c) => (
          <Link
            key={c.to}
            to={c.to}
            data-testid={`nav-${type}-${c.to.split("segment=")[1]}`}
            className="block px-5 py-4 border-b last:border-b-0 border-ink-600 font-mono-tech text-[10px] text-steel-200 hover:text-copper hover:bg-ink-700 transition-colors"
          >
            ⏵ {c.label}
          </Link>
        ))}
      </div>
    </div>
  );
}

function MobileDropdown({ label, type, children, onItemClick }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-ink-600">
      <button
        type="button"
        data-testid={`nav-mobile-dropdown-${type}`}
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-4 font-mono-tech text-xs text-steel-200"
      >
        {label}
        <ChevronDown size={14} className={`${open ? "rotate-180" : ""} transition-transform`} />
      </button>
      {open && (
        <div className="bg-ink-900 border-t border-ink-600">
          {children.map((c) => (
            <Link
              key={c.to}
              to={c.to}
              onClick={onItemClick}
              className="block px-8 py-3 font-mono-tech text-[10px] text-steel-300 border-b last:border-b-0 border-ink-700 hover:text-copper"
            >
              ⏵ {c.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  return (
    <header
      data-testid="site-navbar"
      className="sticky top-0 z-40 border-b border-ink-600 bg-ink-900/90 backdrop-blur"
    >
      <div className="max-w-[1400px] mx-auto grid grid-cols-12 items-center">
        <Link
          to="/"
          data-testid="nav-logo"
          className="col-span-7 md:col-span-3 flex items-center gap-3 px-5 py-3 border-r border-ink-600 group"
        >
          <span className="grid place-items-center h-11 w-16 md:w-14 bg-ink-800 border border-ink-600 overflow-hidden group-hover:border-copper transition-colors shrink-0">
            <img src={LOGO_URL} alt="Washington Rotating Electrics" className="max-w-full max-h-full object-contain" />
          </span>
          <div className="leading-none">
            <div className="font-display text-base tracking-tight chrome-text">{COMPANY.short}</div>
            <div className="font-mono-tech text-[9px] text-steel-400 mt-1">STARTERS & ALTERNATORS</div>
          </div>
        </Link>

        <nav className="hidden md:flex col-span-7 items-center">
          {links.map((l) =>
            l.children ? (
              <DesktopDropdown key={l.label} {...l} />
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-5 py-5 font-mono-tech text-[11px] border-r border-ink-600 transition-colors ${
                    isActive ? "text-copper bg-ink-800" : "text-steel-200 hover:text-copper"
                  }`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ),
          )}
        </nav>

        <div className="hidden md:flex col-span-2 items-center justify-end gap-3 px-4">
          <Link to="/quote" data-testid="nav-quote-cta" className="btn-copper !px-4 !py-2">
            Quote
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden col-span-5 flex justify-end px-5 py-4"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-ink-600 bg-ink-800" data-testid="nav-mobile-panel">
          {links.map((l) =>
            l.children ? (
              <MobileDropdown key={l.label} {...l} onItemClick={() => setOpen(false)} />
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `block px-5 py-4 font-mono-tech text-xs border-b border-ink-600 ${
                    isActive ? "text-copper" : "text-steel-200"
                  }`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ),
          )}
          <Link
            to="/quote"
            onClick={() => setOpen(false)}
            className="block px-5 py-4 bg-copper text-ink-900 font-mono-tech text-xs font-bold"
          >
            Request Quote →
          </Link>
        </div>
      )}
      {loc.pathname !== "/" && (
        <div className="border-t border-ink-600 bg-ink-800/60">
          <div className="max-w-[1400px] mx-auto px-5 py-3 font-mono-tech text-[10px] text-steel-400">
            <Link to="/" className="hover:text-copper">/ HOME</Link>
            <span className="mx-2">›</span>
            <span className="text-steel-100">
              {loc.pathname.replace("/", "").toUpperCase().replace(/\//g, " › ")}
            </span>
          </div>
        </div>
      )}
    </header>
  );
}
